<template>
<div class="chapter">
<div class="inner_page">
<h3 class="calibre4">2. Plato.</h3>
<p class="center"><b class="calibre7">a. Einleitendes.</b></p>
<p class="calibre2">Platos Lebensanschauung zu zeichnen bildet wohl die schwerste
Aufgabe unserer ganzen Untersuchung. Vornehmlich deshalb, weil die
weltüberlegene Persönlichkeit, die seine Werke vor Augen stellen,
verschiedenartige Antriebe, ja schroffe Gegensätze enthält. Plato
ist an erster Stelle der königliche Denker, der mit siegreicher
Kraft durch allen Schein hindurch und über alles Bild hinaus zu
einem unwandelbaren Wesen der Dinge vordringt, damit der Welt eine
Tiefe gibt und das Gesamtbild der Wirklichkeit umgestaltet. Dieser
Denker ist aber zugleich ein Künstler von Gottes Gnaden, den es
fortwährend zum Gestalten und Schauen drängt, dessen hochgestimmte
Phantasie alles Gedankenwerk mit glanzvollen Bildern umrankt, ja
durchflicht, dem alle Fülle der sichtbaren Welt zu einem
leuchtenden Spiegel seiner Gedanken wird. <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page30" title="adelheidis/Nick55"></a> In alles Dichten und Denken
aber legt Plato eine kraftvolle moralische Persönlichkeit, die
alles prüft und läutert; als echt und wertvoll gilt ihm nur, was
das Ganze der Seele fördert, indem es befestigt, reinigt, veredelt.
»Alles Gold über der Erde und unter der Erde wiegt die Tugend nicht
auf«. Über allen Einzelbewegungen endlich steht das Ganze einer
selbstwüchsigen, schöpferischen Persönlichkeit, die überall Leben
hervorruft, wo sie mit ihrer Arbeit einsetzt, die wunderbar zu
malen versteht und ihre Schöpfungen leibhaft vor Augen stellt.
Tiefe und fruchtbare Gedanken werden wie aus einem unerschöpflichen
Füllhorn ausgestreut.</p>
<p class="calibre2">Aber was für die Persönlichkeit eine Größe, das macht das
Verständnis ihres Werkes schwer. Jeder einzelne der verschiedenen
Züge ist viel zu selbständig, um sich den anderen unterzuordnen,
gegenseitige Hemmungen und Durchkreuzungen liegen nahe und lassen
auch die Deutung des Ganzen schwanken.</p>
<p class="calibre2">Diese Mannigfaltigkeit der Bewegungen macht das Dunkel besonders
schmerzlich, das über der Reihenfolge der platonischen Schriften
und über der inneren Geschichte des Mannes liegt. Wohl heben sich
gewisse Hauptphasen deutlich heraus; an welcher Stelle sich aber
die einzelnen Abschnitte und Übergänge finden, und was zu den
verschiedenen Zeiten der Hauptzug der Bewegung war, das ist trotz
unsäglicher Arbeit der gelehrten Forschung noch immer so wenig zu
voller Klarheit gebracht, daß gewagte Vermutungen dabei nicht zu
entbehren sind; solche aber muß unsere Darstellung meiden. So soll
sie sich vornehmlich an die Werke halten, welche Plato als den
Schöpfer der Ideenlehre zeigen. Denn in ihr erreicht er seine
größte Selbständigkeit, und mit ihr hat er am stärksten auf die
Menschheit gewirkt.</p>
<p class="center">*</p>
<p class="center"><b class="calibre7">b. Die Ideenlehre.</b></p>
<p class="calibre2">Platos Streben bekundet durchgängig eine tiefe Unzufriedenheit
und einen schroffen Gegensatz zu seiner gesellschaftlichen
Umgebung. Es ist zunächst die athenische Demokratie, die seinen
Zorn erweckt, das Verhalten der »Vielen«, die ohne Ernst und ohne
Einsicht nach schwankender Lust und Laune über die wichtigsten
Dinge beschließen und durch lärmende Massenwirkungen die Seelen der
Jugend verleiten. Aber dem Denker wird die Not seiner Zeit und
seines Kreises ein Problem aller Orte und Zeiten. Jegliches <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page31" title="adelheidis/Nick55"></a> menschliche
Tun, das sich auf sich selber stellt und eine Verbindung mit
überlegenen Ordnungen ablehnt, gilt ihm als nichtig und irrig; es
kennt kein wahrhaftiges Sein, vom flüchtigen Schein beherrscht
bietet es von Tugend und Glück nicht mehr als einen Schein, einen
eitlen und selbstgefälligen Schein. So befreit sich der Denker vom
bloßen Menschen und flüchtet zum All, vom Alltagsgetriebe mit
seinen niedrigen Gelüsten heißt er aufschauen zu den ewigen, aller
Unbill fremden Ordnungen, die der Anblick des Himmelgewölbes
einleuchtend vor Augen stellt; der Anschluß an sie wird unser Leben
weiter und wahrer, reiner und beständiger machen. So ein Streben
über den Menschen hinaus, eine Wendung zum All als dem Standort
wahrhaftigen Lebens.</p>
<p class="calibre2">Aber dies neue Leben begegnet sofort einem scheinbar
unüberwindlichen Hemmnis. Die sinnliche Welt war schon durch
Gedankenarbeit in ihrer Handfestigkeit erschüttert, namentlich war
ihr unablässiges Anderswerden, war der regellose Fluß der Dinge
viel zu deutlich erkannt, um dem Leben und Streben einen sicheren
Halt zu gewähren. Bildet das Reich der Sinne die einzige Welt, so
kann die Wendung zum All dem Denken kein Sein erschließen, dem
Leben keine Festigkeit geben. Aber ist jenes Reich das Ganze der
Wirklichkeit? Sokrates' Lehre vom Denken und von den Begriffen
hatte neue Wege gewiesen. In den Begriffen war gegenüber den
schwankenden Meinungen der Einzelnen etwas Beharrendes und
Allgemeingültiges erkannt, für Sokrates freilich nur innerhalb
unseres eigenen Gedankenkreises. Plato aber, seiner Gesamtart nach
mehr aufs Weite und Kosmische gerichtet, tut hier einen
beträchtlichen Schritt vorwärts. Der Begriff, so meint er, könnte
nicht wahr sein, reichte er nicht über den Menschen hinaus, und
übermittelte er nicht eine Wirklichkeit der Dinge. Das entspricht
der griechischen Denkart, die den Menschen von der Welt nicht
ablöst und ihr entgegensetzt, sondern ihn ihr eng verbunden hält,
welche die Tätigkeit dem Gegenstand anschmiegt und im Befunde des
Seelenlebens eine Mitteilung der Dinge sieht. Das kleine Leben
folgt hier dem großen, denn nicht entfacht und nährt sich, so meint
Plato, das Feuer des Alls aus dem Feuer bei uns, sondern es hat von
jenem das meine und das deine und das aller Lebewesen was immer es
hat. Hängen wir aber so an den Dingen, und schöpft die Seele allen
Gehalt aus dem All, so ist im Befunde der kleinen Welt die große
mit Sicherheit zu ergreifen. Nun ist für Plato ausgemacht, <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page32" title="adelheidis/Nick55"></a> daß gegenüber
der schwankenden Meinung ein Wissen mit festen Begriffen besteht;
so gibt es sicherlich auch im All eine unwandelbare Wirklichkeit
übersinnlicher Art, ein Reich von Gedankengrößen jenseit der
fließenden Sinnenwelt.</p>
<p class="calibre2">Auf diesem Wege kommt Plato zum Kern seiner philosophischen
Überzeugung, zu seiner Ideenlehre. Das Wort Idee, ursprünglich
Aussehen, Bild, Gestalt bedeutend, erhält und behauptet von hier
aus einen technischen Sinn, es bezeichnet nun das Gegenstück des
Begriffes in der Welt der Dinge, ein wesenhaftes, wandelloses, nur
dem Gedanken zugängliches Sein. Die Ideenlehre befestigt und
objektiviert unsere Begriffe, eine kühne logische Phantasie trägt
diese über den menschlichen Kreis hinaus in das All und verkörpert
sie zu selbständigen Größen uns gegenüber. Die Gedankenwelt aber,
die damit entsteht, gilt Plato als der Grundbestand aller
Wirklichkeit, als Trägerin der Welt, die uns sichtbar umfängt.</p>
<p class="calibre2">Das ist eine Umwälzung und Umwertung gewaltiger Art. Wohl hatte
auch vorher schon die Arbeit hervorragender Denker die nächste Welt
zu einer niederen, ja zu einem bloßen Scheine herabgesetzt, aber
diese Wendung war noch nicht mit dem Ganzen dieser Welt
ausgeglichen und konnte daher auf weitere Kreise keinen Einfluß
gewinnen. Plato dagegen hat eine gründliche Auseinandersetzung
vollzogen und zugleich alle Begriffe von der Wirklichkeit
wesentlich umgestaltet. Zum Ersten, Gewissesten, unmittelbar
Gegenwärtigen wird jetzt eine Welt, die sich nur dem Denken
erschließt. Nicht nach der Stärke des sinnlichen Eindrucks, sondern
nach der geistigen Durchsichtigkeit bemißt sich jetzt die Nähe und
Erkennbarkeit der Dinge; da das sinnliche Dasein mit seiner
Räumlichkeit sich reinen Begriffen verschließt, so bleibt es bei
aller Handgreiflichkeit in trübem Dämmerschein, während die Ideen
sich sonnenhell durchleuchten lassen. Bei solcher Wandlung bildet
die Seele unser echtes Wesen, der Körper wird ein bloßer Anhang, ja
ein fremdes und niederes Sein. Auch das Streben wird damit ganz und
gar unsinnlichen Gütern zugewandt.</p>
<p class="calibre2">Dieser Gedankenwelt gibt die volle Herrschaft des Erkennens eine
eigentümliche Färbung. Nur das Erkennen, das Auge des Geistes,
führt vom Schein der Sinne zum Reich des Wesens. Dies Reich
wesenhaften Seins gilt Plato aber zugleich als das Gute, als das an
sich selbst Wertvolle, als das, woraus alles stammt, was <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page33" title="adelheidis/Konmax"></a> bei uns als
gut geschätzt wird. Daß so das Wesenhafte mit dem Guten
zusammenfällt, daß die Dinge um so höher stehen und um so besser
sind, je mehr sie an echtem Sein teilhaben, dies Zusammenfallen von
Wesenhaftem und Wertvollem erzeugt einen festen Glauben an eine
Vernunft im Grunde der Wirklichkeit; ein solcher Glaube gibt dem
Menschen eine sichere Überlegenheit gegen alles Dunkle und Böse im
nächsten Befunde der Welt, er gibt zugleich dem Streben den
stärksten Antrieb, sich zu der Höhe aufzuschwingen, wo die
Widersprüche sich lösen und das Leben zu reiner Freude wird. Solche
Zusammenhänge kennen kein radikales Böse; so gewiß das Niedere
herabzieht und entstellt, das Gute zerstören kann es nicht. So
rechtfertigt sich bei allem schweren Ernst eine zuversichtliche und
freudige Lebensstimmung, eine Stimmung freilich, die durch geistige
Arbeit erkämpft ist, nicht von Natur zufällt.</p>
<p class="calibre2">Die Forderung des Aufstiegs erhält bei Plato dadurch einen
besonderen Charakter, daß neben der höheren Welt eine niedere
dauernd verbleibt, in der die Ideen nur getrübt zur Wirkung
gelangen, in der sich daher der Mensch nicht heimisch machen darf.
Mit solcher Scheidung zweier Welten hat Plato eine gewaltige
Bewegung hervorgerufen und das Lebensproblem auf eine Bahn geführt,
die, immer wieder bestritten, doch nicht aufgegeben ward; an dieser
Stelle vornehmlich treibt er die Geister zu schroffer Scheidung, da
es hier keine Möglichkeit einer Verständigung gibt.</p>
<p class="calibre2">Das ist der tiefste Kern der platonischen Überzeugung, aber
zugleich sei gegenwärtig, daß seine künstlerische Art jenes Streben
zum unwandelbaren Sein wesentlich ergänzt und weiterbildet. Das
unsinnliche Wesen der Dinge erscheint zugleich als die von aller
Materie befreite Gestalt, als die Form, welche von innen heraus,
nicht von draußen auferlegt, die Elemente zusammenhält, gegenüber
allem Werden und Vergehen der Einzelwesen mit ewiger Jugend beharrt
und immer von neuem das sinnliche Dasein belebend und veredelnd
ergreift. Das Walten einer solchen Form findet der Denker in allen
Reichen und Stufen der Wirklichkeit, in der großen wie in der
kleinen Welt, in der Natur wie in der Seele; mit der Herausbildung,
die hier erfolgt, wird das Weltphänomen der Gestaltung zuerst vom
Denken angeeignet und zugleich die formenstrenge und erhabene Kunst
philosophisch begründet, welche die Höhe des Griechentums zeigt. So
gesellt sich zur Wesenhaftigkeit die Schönheit; diese aber wird ein
Band, das unsinnliche und sinnliche Welt zusammenhält, <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page34" title="adelheidis/lac"></a> die dem Streben
nach Wahrheit auseinander zu fallen drohen. Denn ihr gilt das
Niedere nicht als ein schlechthin Nichtiges, sondern nur als ein
Unvollkommenes, als ein Abbild, das wohl hinter dem Urbild
zurückbleibt, das aber doch zu ihm hinweist.</p>
<p class="calibre2">Mag an der Ideenlehre Platos manches angreifbar sein, sie
enthält eine Grundwahrheit, die sich nicht wieder aufgeben läßt.
Das ist die Überzeugung, daß ein Reich der Wahrheit jenseit des
Beliebens der Menschen besteht, daß die Wahrheiten nicht wegen
unserer Zustimmung, sondern durch sich selber gelten, daß ihr Reich
alles menschliche Meinen und Mögen weit überragt. Ohne solche
Überzeugung gibt es keine Selbständigkeit der Wissenschaft und des
gesamten Geisteslebens; nur jene überlegene Wahrheit macht Gesetze
und Normen möglich, die das menschliche Dasein erhöhen, indem sie
es binden und richten. Wer immer daran festhält, der steht in
Verbindung mit Plato.</p>
<p class="center">*</p>
<p class="center"><b class="calibre7">c. Die Lebensgüter.</b></p>
<p class="calibre2">Die platonische Lebensführung, wie sie aus der Ideenlehre
hervorgeht, ist in ihren Grundzügen einfach. Alles geistige Leben
ruht auf wissenschaftlicher Einsicht, es sinkt und verfällt, sobald
es sich davon losreißt. In seiner näheren Durchbildung aber strebt
es zu künstlerischer Gestaltung, zu plastischem Ebenmaß und
durchgebildeter Harmonie. So verbinden und durchdringen sich hier
zu gegenseitiger Förderung die beiden Hauptrichtungen des
griechischen Lebens: das Verlangen nach klarem Erkennen und das
nach anschaulichem Gestalten. Plato bildet damit die Höhe der
geistigen Arbeit seines Volkes, ein enger Zusammenhang mit diesem
ist unverkennbar, von allen Denkern ist Plato am meisten Grieche.
Aber zugleich findet sein tiefes und kräftiges Wesen die Darbietung
der Umgebung durchaus unzulänglich, es zwingt ihn, mit dieser zu
brechen und in unermüdlichem Kampf höhere Güter aufzudecken, höhere
Ziele vorzuhalten.</p>
<p class="calibre2">Ein Bruch mit dem Durchschnittsleben liegt vor allem in der
Berufung der Wissenschaft zur ausschließlichen Führung des Lebens;
wissenschaftliche Einsicht hat alle Betätigung zu tragen, nichts
gilt als echt, was sich nicht auf Gedankenarbeit gründet. Nur die
Einsicht erzeugt echte Tüchtigkeit. Denn nur sie befreit von dem
Schein und der Äußerlichkeit der landläufigen Tugend, nur sie
verankert die Tugend im eigenen Wesen des Menschen und macht <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page35" title="adelheidis/lac"></a> sie zugleich zur
freien Tat des Menschen. Denn was gewöhnlich Tugend heißt, in
Wahrheit sich aber von körperlichen Fertigkeiten kaum
unterscheidet, ist mehr ein Erzeugnis der gesellschaftlichen
Umgebung, ein Werk von Sitte und Übung, als eigene Tat und
Entscheidung.</p>
<p class="calibre2">Auch das Schöne muß sich in das Element des Gedankens tauchen,
um sich von der gemeinen, niedere Lust begehrenden Art zu befreien.
Denn erst jenes vertreibt aus ihm, was dem bloßen Reiz und Genuß
angehört; erst in Abstreifung alles Körperhaften, in Erhebung zu
reiner Geistigkeit kann es sich selbst vollenden. Dann ist es nach
Winckelmanns Ausdruck »wie ein aus der Materie durchs Feuer
gezogener Geist«.</p>
<p class="calibre2">Mit der Aneignung des Schönen gewinnt aber die Denkarbeit selbst
Antriebe fruchtbarster Art. Sie vermag damit bei aller Abweisung
roher Sinnlichkeit eine Festigkeit und Gegenständlichkeit zu
bewahren, das Erkennen erscheint eben auf seiner Höhe hier als ein
Erblicken eines beharrenden Vorwurfs, es entsteht der Begriff einer
geistigen Anschauung, der einen eigentümlich griechischen Sinn der
Anschauung aufnimmt und weiterbildet. Anschauen bedeutet hier nicht
ein tatloses Aufnehmen eines fremden Gegenstandes, sondern eine
Verbindung von Tätigkeit und Vorwurf zu lebendiger Wechselwirkung
und gegenseitiger Durchdringung, so daß unablässig Leben vom einen
zum anderen überströmt. Das setzt aber eine Verwandtschaft des
Wesens voraus, unser Auge muß sonnenartig sein, um im Licht der
Sonne Dinge zu sehen, unsere Vernunft den ewigen Beständen
verwandt, um im Licht der Ideen Ewiges zu fassen.</p>
<p class="calibre2">In solchem Zusammenhange nähert sich die Anschauung der Liebe,
dem Eros. Das Erkenntnisverlangen enthält ein Suchen des
Wesensverwandten, nur zusammen mit einem solchen, nur in
gegenseitiger Belebung kann das nach Wahrheit dürstende Wesen sein
Ziel erreichen und Unvergänglichkeit finden. So wird die Forschung
zum geistigen Schaffen; Geist und Wahrheit kommen nicht fertig an
uns, sie entstehen erst durch Berührung unseres Strebens mit der
Vernunft des Alls. Mit dem Begriff des geistigen Schaffens aber,
wie er hier der Wissenschaft aufgeht, wird der Forschung selbst ein
künstlerisches Element eingepflanzt und ein enges Bündnis von
Wahrheit und Schönheit besiegelt.</p>
<p class="calibre2">Wie aber das Schöne, so wird auch das Gute der Forschung <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page36" title="adelheidis/lac"></a> innig verbunden
und verschlungen, zugleich aber über die gewöhnliche Fassung
hinausgehoben. Für Plato ist die Philosophie keine bloße Theorie im
späteren Sinne, sondern ein Aufrütteln des ganzen Wesens, ein
Erheben des ganzen Menschen vom Schein zur Wahrheit, ein Erwachen
aus dem Schlummer des Alltagslebens, ein Abwerfen aller
Sinnlichkeit. Das Streben zur Wahrheit wird unmittelbar eine
sittliche Tat; denn es ist der Trieb der Wahrhaftigkeit, der mit
allem Scheine brechen und echtes Wesen suchen heißt. Auch insofern
gehören Wahres und Gutes zusammen, als das höchste Gut unwandelbar
sein muß, etwas Unwandelbares aber nur die Forschung eröffnet.</p>
<p class="calibre2">Enger noch verbinden sich Gutes und Schönes unter gegenseitiger
Fortbildung. Die Fassung des Schönen zeigt Plato als einen Sohn
seines Volkes und seiner Zeit, er gibt eine philosophische
Begründung des Klassischschönen, das damals auf seiner Höhe stand.
Das Schöne ist hier vornehmlich plastischer Art, es verlangt eine
scharfe Scheidung alles Mannigfachen, eine deutliche Entfaltung
jedes Teiles, eine kräftige Zusammenfassung zum Ganzen eines
Werkes. Wo immer ein Ganzes erstrebt wird, da sollen die Teile das
anfängliche Chaos verlassen, jeder eine besondere Aufgabe erhalten
und seine Grenzen gegen die übrigen gewissenhaft wahren; dann aber
soll sich die Mannigfaltigkeit ordnen, abstufen und zu einem
Kunstwerk verbinden, dessen Ebenmaß und Harmonie eine reine und
edle Freude erzeugt. Jene Ordnung wird nicht von außen auferlegt,
sondern von innen her bereitet, die Bildung zum Schönen entspringt
aus dem eigenen Leben und Streben, bei aller Ruhe ist das Kunstwerk
zugleich ein beseelter Organismus. So das Gedankenbild des
Klassischschönen, eines Schönen von festen, ja strengen
Verhältnissen und klaren Abmessungen, von umgrenzter und
durchsichtiger Gestalt, dabei aber voll inneren Lebens.</p>
<p class="calibre2">Ein derartiges Schöne erkennt der Blick des Forschers durch
allen trüben Schein hindurch sowohl in der weiten Welt als im
menschlichen Bereiche; Grenze und Ordnung, Ebenmaß und Harmonie
leuchten ihm überall entgegen. So aus dem Himmelsgewölbe mit dem
unwandelbaren Beharren der Gestirne in aller rastlosen Bewegung, so
auch aus dem inneren Gefüge der Natur, das Plato streng nach
mathematischen Verhältnissen ordnet.</p>
<p class="calibre2">Was aber draußen in sicherer Wirkung steht, das wird beim
Menschen zur Forderung und Tat; die wichtigste aller Harmonien
<a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page37" title="adelheidis/lac"></a> ist die
Harmonie des Lebens, zu der vornehmlich die hellenische Art berufen
dünkt. Auch unser Wesen mit seiner Vielheit von Trieben ist von
Natur in Grenzen und Ordnung gewiesen. Aber zur vollen Belebung der
Mannigfaltigkeit und zur Herstellung des Ebenmaßes bedarf es
eigener Tat, die auf rechter Einsicht ruht. Mit Hilfe solcher
Einsicht gilt es das Durcheinander der Triebe zu überwinden, alle
in uns angelegte Kraft auszubilden, ihre Mannigfaltigkeit scharf
gegeneinander abzugrenzen, schließlich alle Leistungen in ein
Lebensgefüge zusammenzufassen. Alles Grenzenlose und Unbestimmte
ist hier verpönt, alle Bewegung hat ein festes Ziel, auch die
Kräfte lassen sich nicht ins Endlose steigern. Wenn jeder an seiner
Stelle das Seine tut, dann fährt das Ganze am besten, dann wird das
Leben schön und freudvoll in sich selbst. Solcher Überzeugung
entspricht ein eigentümliches Bildungsideal. Der Mensch wolle nicht
sich zu allem bilden und alles mögliche leisten, sondern er
ergreife Ein Ziel und widme ihm seine ganze Kraft. Weit besser ist
es eines gut, als vieles unzulänglich zu tun, Nichtwissen ist
besser als Vielwisserei. So ein aristokratisches Ideal in bewußtem
Gegensatz zu dem demokratischen einer Erziehung aller für alles,
einer möglichst vielseitigen und allen gemeinsamen Bildung.</p>
<p class="calibre2">Indem so die Harmonie des Lebens aus unserer Tat hervorgeht,
indem sie unser Wollen und unsere Gesinnung gewinnt, wächst sie zu
einem ethischen Werk, zur Tugend der Gerechtigkeit. Das nämlich ist
das Gerechte, das Seine zu tun und jedem das Seine zu geben, statt
in fremde Kreise überzugreifen, treu die besondere Aufgabe zu
erfüllen, die Natur und Geschick uns stellen. Demnach ist die
Gerechtigkeit nichts anderes als die ins eigene Wollen aufgenommene
Harmonie, als solche wird sie dem Denker in Einklang mit seinem
Volke zum Zentralbegriff des sittlichen Lebens, zur allumfassenden
Tugend. Ihm steigert sie sich weiter zu einer sittlichen
Weltordnung: dem Handeln wird schließlich das Ergehen entsprechen,
früher oder später muß, wenn nicht in diesem, so in einem anderen
Leben das Gute, seinen Lohn, das Böse seine Strafe finden.</p>
<p class="calibre2">Besteht demnach die Tugend in der Belebung und harmonischen
Gestaltung des eigenen Wesens, so wird das Streben nach ihr eine
unablässige Befassung des Menschen mit seiner eigenen Seele, eine
Befreiung von allem Druck der gesellschaftlichen Umgebung. Wenn
diese über die südlichen Völker besonders große Macht besitzt, so
<a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page38" title="adelheidis/lac"></a> hat sie
dort auch in selbstwüchsigen Persönlichkeiten die kräftigste
Gegenwirkung gefunden. – Jene Verinnerlichung der Aufgabe
macht nämlich zum Hauptziel, nicht anderen Menschen, sondern sich
selbst zu gefallen, gut nicht zu scheinen, sondern zu sein. Wie
erst diese Wendung zum eigenen Wesen das Leben selbständig und
wahrhaftig macht, so eröffnet sie ihm auch ein unvergleichlich
höheres Glück, reine und echte Freudigkeit. Auf Glück verzichtet
Platos kräftige und mutige Natur keineswegs, aber sie findet das
Glück nicht mit der Menge in Ereignissen und Erfolgen äußerer Art.
Es in der Tätigkeit selbst aufzusuchen, das gestattet jetzt die
Eröffnung eines lebenumfassenden Werks in der Seele. Gilt es doch
den ganzen Umkreis erst zu beleben und alle Mannigfaltigkeit zu
einem Zusammenklang zu bringen. An dem Gelingen dessen liegt der
Erfolg oder Mißerfolg unseres Lebens und zugleich unser Glück oder
Elend. Denn nach Plato wird alles an Harmonie und Disharmonie
Vorhandene vom Handelnden deutlich geschaut und kräftig empfunden,
es wird ungetrübt so empfunden, wie es vorhanden ist. So kommt der
wirkliche Stand der Seele in Freud und Leid zu reinem Ausdruck, die
Gerechtigkeit erzeugt mit ihrer Harmonie zugleich die Seligkeit,
ein allen anderen Gütern weit überlegenes Glück, die
Ungerechtigkeit aber mit ihrer Disharmonie, ihrem Zerreißen und
Sichverfeinden unserer eigenen Natur eine unerträgliche Qual.</p>
<p class="calibre2">Dieser untrennbare Zusammenhang von Tätigkeit und Glück bildet
die Höhe der Weisheit eines lebenskräftigen Volkes; dies ist auch
das Ideal, das der griechischen Philosophie bis zu ihrem letzten
Atemzuge vorgeleuchtet hat. Bei solcher Überzeugung bildet das
Glück wohl die naturgemäße Folge, nicht aber den Beweggrund des
Handelns; alles kleinliche Mühen um Lohn wird verscheucht, wo das
Gute seinen Wert in sich selbst, in seiner inneren Schönheit hat,
deren Schauen den Menschen beglückt. Derart das Glück im Innern
begründen, das heißt zugleich die Macht des Schicksals über den
Menschen brechen. Alle Dürftigkeit und aller Widerspruch der
äußeren Verhältnisse schädigt hier nicht den durch das eigene Tun
erzeugten Seelenstand, ja der Kontrast muß seine Überlegenheit und
Selbstgenügsamkeit noch verstärken und bewußter machen. Bei aller
Gunst der Geschicke bleibt der Schlechte elend, ja er wird dadurch
noch elender, indem das Böse um so üppiger aufwuchern wird; dem
Guten aber erweist sich in allen Hemmungen und Leiden <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page39" title="adelheidis/lac"></a> erst recht die
innere Herrlichkeit seines Lebens. Aus solcher Denkart entwirft
Plato ein packendes Bild vom leidenden Gerechten, der bis zum Tode
verfolgt wird und mit dem Schein der Ungerechtigkeit behaftet
bleibt, dessen innere Hoheit aber inmitten solcher Anfechtung aufs
hellste strahlt; ein Bild, das in der äußeren Annäherung an
christliche Vorstellungen den inneren Abstand der beiden Welten um
so deutlicher erkennen läßt.</p>
<p class="center">*</p>
<p class="center"><b class="calibre7">d. Weltflucht und Weltverklärung.</b></p>
<p class="calibre2">Wesentlich ist der platonischen Überzeugung die Scheidung zweier
Welten; zwischen dem Reich der Wahrheit und dem nächsten Dasein
bleibt eine unüberwindliche Kluft, welche auch der Verlauf der
Geschichte nicht zu verringern vermag. Je entschiedener der Denker
auf der Selbständigkeit und Unvergleichlichkeit der geistigen Güter
besteht, desto überzeugter sieht er in ihnen eine eigene Welt
gegenüber einer Welt von geringerer Wahrheit und Vollkommenheit.
Was folgt aus solcher schroffen Scheidung für unser Handeln? Kann
es beide Welten miteinander umfassen, oder soll es sich
ausschließlich der höheren widmen? Dieses allein scheint
folgerichtig. Denn warum sollen wir unsere Kraft verteilen, wo die
Welt des wesenhaften Seins unsere ganze Hingebung fordert, warum
uns um das Vergängliche mühen, wenn der Weg zum Unvergänglichen
offensteht, warum im Dämmerlicht der Abbilder bleiben, wenn die
Urbilder sich ungetrübt schauen lassen? In diese Richtung treibt
namentlich Platos Verlangen nach einem wesenhaften Sein; gegenüber
seiner Ewigkeit und Einfachheit sinkt das bunte Reich der Sinne zu
einem trügerischen Schein herab; so wird es zur Aufgabe der
Aufgaben, sich diesem Schein zu entwinden und alle Liebe wie alle
Sorge dem unwandelbaren Sein zuzuwenden. Damit unverkennbar ein
Lebenstypus der Weltflucht.</p>
<p class="calibre2">Augenscheinlich ist, von jener Höhe aus gesehen, die Nichtigkeit
und Scheinhaftigkeit des Lebens, das uns zunächst umfängt. Es ist
nicht nur im Einzelnen voller Mängel, sondern in der Gesamtanlage
bis zum Grunde verfehlt. Hier, wo die Sinnlichkeit alles
herabzieht, findet sich nirgends ein reines Glück, alles Edle wird
entstellt und verkehrt, nicht der Sache, sondern dem Schein dient
alles menschliche Verlangen, der unstete Fluß der Erscheinungen
duldet kein beständiges Gut. In die finstere Höhle der
Sinnlichkeit, in die wir <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page40" title="RaiHoff/adelheidis"></a> gebannt sind, wirft die lichte Welt der
Wahrheit nur flüchtige Schattenbilder. Wenn nun das Denken einen
Weg zur Befreiung aus solcher Lage eröffnet, sollten wir ihn nicht
freudig betreten, sollten wir nicht mutig alles von uns werfen, was
uns im Reich des Scheines festhält? Es hält uns aber alles fest,
was dort als ein Gut gepriesen wird: Schönheit, Reichtum,
Körperkraft, angesehene Verwandtschaft usw.; so muß der Freund der
Wahrheit sich davon befreien, ja es vergessen. Die Seele befindet
sich im Körper wie in einem Kerker, ja einem Grabe. Daraus erretten
kann sie nur eine gänzliche Meidung von Lust und Begier, von
Schmerz und Furcht. Denn diese Affekte schmieden sie an den Körper
und lassen sie der Welt des Sinnenscheins volle Wahrheit
zuerkennen. Die Affekte beherrschen aber die Seele, solange die
gewöhnlichen Schicksale sie noch erregen und bewegen; so gilt es
eine völlige Gleichgültigkeit dagegen zu erringen und das Glück
allein in die geistige Tätigkeit, d. h. in das Erfassen echten
Seins zu setzen. Alle Schläge des Schicksals gleiten ab von einer
weisen und tapferen Seele, die ewige Güter kennt. »Das Beste ist,
sich bei Unglücksfällen ruhig zu verhalten und nicht aufzuregen, da
weder bei solchen Dingen Gut oder Böse deutlich ist, noch wer es
schwer nimmt, dadurch etwas gewinnt, noch überhaupt etwas von den
menschlichen Dingen großen Eifer verdient«. Auch bei den
Schicksalen anderer sollen wir nicht weibisch jammern, sondern
mannhaft dem Erkrankten helfen, den Gefallenen aufzurichten suchen.
Einen vollen Sieg erlangt nur, wer alles Gefühlsleben abwirft und
sich über die Welt der menschlichen Freuden und Leiden tapferen
Sinnes hinaushebt. Solche Ablösung des Lebens von dem sinnlichen
Dasein nimmt dem Tode jeglichen Schrecken, er wird eine »Befreiung
von aller Irrung und Unvernunft und Furcht und wilden
Leidenschaften und allen anderen menschlichen Übeln«. Nur der
körperfreien Seele erschließt sich die volle Wahrheit, denn nur
Reines darf das Reine berühren. So wird die Ablösung vom
Sinnlichen, die Vorbereitung zum Sterben zur Hauptaufgabe der
Philosophie; sie bedeutet jetzt ein Erwachen aus schwerem Traum zu
lichter Klarheit, eine Rückkehr aus der Fremde zur Heimat.</p>
<p class="calibre2">So eine Weltflucht im vollen Sinne des Wortes. Gewiß ist diese
platonische Weltflucht von der mittelalterlichen weit verschieden.
Es wird nur das sinnliche Dasein aufgegeben, nicht die Welt
überhaupt, und das erstrebte ewige Sein befindet sich nicht <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page41" title="RaiHoff/adelheidis"></a> in
jenseitiger Ferne als ein Gegenstand gläubiger Hoffnung, sondern es
umfängt die wesensverwandte Seele auch in diesem Leben mit
eindringlicher Gegenwart; auch wird es nicht von einer höheren
Macht aus Gnade geschenkt, sondern durch eigene Tätigkeit
errungen.</p>
<p class="calibre2">Aber auch so bleibt ein schroffer Bruch mit der nächsten Lage
und der menschlichen Gemeinschaft. Einsam steht hier der Denker auf
steiler Höhe und einförmig wird seine Welt. Mit der Abweisung aller
Schmerzen und Freuden, aller Sorgen und Ziele der Menschheit droht
das Dasein alle Fülle und Anschaulichkeit zu verlieren und aller
Reichtum der Dinge in den Abgrund einer gestaltlosen Ewigkeit zu
versinken.</p>
<p class="calibre2"> </p>
<p class="calibre2">In dieser Weltflucht haben wir den echten Plato und den
konsequenten Plato, keineswegs aber den ganzen Plato. Jene
weltflüchtige Richtung hat bei ihm eine beträchtliche Milderung, ja
eine Gegenwirkung erfahren, wie es bei allen ihren Freunden
geschah, die über dem Individuum nicht das Ganze der Menschheit
vergaßen. Denn mag der einzelne Denker die sinnliche Welt ganz
hinter sich lassen, die Menschheit wird ihm dabei nicht folgen;
schon der Gedanke an die Brüder führte auch Plato zu jener Welt
zurück. Was aber auf indischem und oft auch auf christlichem Boden
nur eine widerwillige Anpassung war, das hat bei Plato auch eine
innere Neigung für sich; ihn, den Griechen wie den Freund, ja
wissenschaftlichen Entdecker der Schönheit, fesseln starke Klammern
an diese Wirklichkeit und treiben ihn dazu, auch in ihr etwas Gutes
zu suchen.</p>
<p class="calibre2">Zur Hebung des sinnlichen Daseins wirkt vornehmlich ein Plato
eigentümliches Streben, zwischen den Gegensatz des Geistigen und
Sinnlichen, des Wesenhaften und Nichtigen, des Ewigen und
Vergänglichen ein Zwischenglied einzuschalten, das ein
Auseinanderfallen des Lebens verhütet. So erscheint als eine
Vermittlung zwischen der Geisteswelt und der sinnlichen Natur die
Seele, indem sie von dort die ewigen Wahrheiten aufnimmt, hierher
aber Leben trägt, so steht bei der Seele selbst zwischen dem Denken
und der Sinnlichkeit der Mut, das kraftvolle Streben, so beim
Erkennen zwischen dem Wissen und dem Nichtwissen die rechte
Meinung. Ähnlich werden auch im Staat und in der Natur die
Gegensätze durch Zwischenglieder verknüpft und alle
Mannigfaltigkeit einer <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page42" title="RaiHoff/adelheidis"></a> Stufenfolge eingefügt. Schließlich bildet
das Schöne selbst ein Bindeglied zwischen der reinen Geistigkeit
und der sinnlichen Welt, indem Ordnung, Maß und Harmonie beide
Reiche verketten und auch dem Niederen an der Göttlichkeit Anteil
geben. Auch die sinnliche Welt ist eine schöne Welt und als solche
unverwerflicher Art. Das neidlose höchste Wesen hat sie so
vollkommen wie möglich nach dem ewigen Urbild gestaltet; in diesem
Gedankengange kann Plato sie den »eingeborenen Sohn Gottes«
nennen.</p>
<p class="calibre2">In solchem Zusammenhange wurzelt die Idee der Vermittlung und
Abstufung, die später gewaltigste Macht erlangt hat und sie bis zur
Gegenwart übt. Ihre Voraussetzungen und Beweggründe liegen bei
Plato deutlich zutage. Ein schroffer, in seinem letzten Grunde
unüberwindlicher Gegensatz, zugleich aber ein starkes Verlangen
nach einer Ausgleichung, nach Herstellung irgendwelches
Zusammenhanges; was anderes kann da helfen als ein Suchen
vermittelnder Mächte? In Verfolgung dieser Gedankenrichtung
entstand jenes hierarchische Stufenreich wie im All so bei der
Menschheit, das einen Hauptzug des Neuplatonismus und der
mittelalterlichen Kirche bildet. Seinem innersten Wesen nach ist es
nicht sowohl christlicher als platonischer Art.</p>
<p class="calibre2">Bei Plato selbst erfolgt eine Verbindung von Höherem und
Niederem nicht bloß durch eine Mitteilung von oben her, sondern
auch durch ein eigenes Aufstreben des Sinnlichen und Menschlichen
zum Göttlichen. Auch durch unsere Welt geht ein Verlangen, an dem
Guten und Ewigen teilzuhaben und dadurch Unvergänglichkeit zu
gewinnen; die Liebe, der Eros, ist nichts anderes als ein solches
Streben nach Unvergänglichkeit. Es erreicht dies Streben seinen
Abschluß erst in der Forschung, welche die volle Einigung mit dem
Wahren und Ewigen herstellt. Aber in aufsteigendem Zuge durchdringt
es das ganze All, und freudig folgt die Betrachtung des Denkers
dieser Stufenleiter der Liebe. Einen verworrenen Drang nach
Unsterblichkeit zeigt schon die untermenschliche Natur in dem
Fortpflanzungstriebe der Wesen, dem Verlangen, bei eigenem
Untergang in den Nachkommen fortzuleben; Unsterblichkeit will das
Mühen der Helden um Ruhm, Unsterblichkeit das Schaffen der Dichter
und Staatsmänner, Herausarbeitung des Ewigen in gegenseitiger
Eröffnung und Mitteilung die Liebe vom Menschen zum Menschen, bis
endlich jener Gipfel der Anschauung und Aneignung des wesenhaften
Seins erreicht wird. <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page43" title="RaiHoff/adelheidis"></a> So wird die Liebe eine dämonische Macht
genannt, welche Göttliches und Menschliches verbindet und unser
Seelenleben unter widerstreitende Antriebe stellt. Sie heißt ein
Kind von Reichtum und Armut, da erst die Mitteilung an den anderen
die Tiefe des eigenen Wesens eröffnet; so verbindet sie Überfluß
und Not, Besitzen und Entbehren. Mit der wunderbaren Zeichnung
solcher Lage und Stimmung wird Plato der erste philosophische
Vertreter romantischer Denkart; tief zieht es ihn dabei in die
Bewegungen des menschlichen Daseins hinein, das die reine
Denkarbeit ihm verleiden möchte.</p>
<p class="calibre2">Solche Wandlungen steigern den Wert der nächsten Welt und den
Reichtum des menschlichen Lebens. Das Erkennen bildet nun nicht
mehr seinen alleinigen Inhalt, sondern nur die beherrschende Höhe,
die Licht und Vernunft überallhin ausstrahlt. Das Niedere aber
gewinnt einen Wert als eine uns Menschen unentbehrliche Stufe zur
Höhe, denn erst allmählich kann unser Auge sich an das Licht der
Ideen gewöhnen. Auch hebt die Idee der Gerechtigkeit und Harmonie
das Niedere, indem sie es zum Glied eines Ganzen macht und ihm
dabei eine besondere Aufgabe zuweist, deren Lösung zur Vollendung
des Gesamtwerks gehört; so in unserer Seele, so auch beim Staate.
Böse wird es jetzt erst, sobald es die Ordnung verkehrt und das
Höhere schädigt; daher ist auch das Sinnliche nicht mehr an sich,
sondern nur in seinem Übermaße verwerflich.</p>
<p class="calibre2">Dem entspricht ein anderes persönliches Verhalten des
Philosophen zu den menschlichen Dingen, nun kann er nicht mehr aus
ferner Höhe sie stolz und kühl behandeln. Vielmehr teilt er in
tiefer Empfindung das gemeinsame Los; wie alles Gute hier seine
Freude, so wird alles Böse sein Schmerz. So treibt es ihn zwingend
zur Förderung des Guten, zur Niederkämpfung des Bösen; der
weltüberlegene Denker wird zum kühnen und leidenschaftlichen
Reformator, er schmiedet eingreifende Pläne zur Verbesserung der
menschlichen Lage und scheut dabei schroffste Umwälzung nicht.
Wurde vorher eine Unterdrückung der Affekte geboten, so wird jetzt
ein edler Zorn für unentbehrlich zum Handeln erklärt. Hier
erscheint der Denker als ein eifriger Streiter, den die Spannung
des Kampfes freudig stimmt, um so mehr, da nach seiner Überzeugung
die Gottheit mit uns kämpft.</p>
<p class="calibre2">Demnach umfaßt die platonische Gedankenwelt eine Weltflucht und
eine Weltverklärung. Aber auch die Weltverklärung erfolgt lediglich
von der Ideenwelt her, dieser entstammt auch alles Gute <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page44" title="adelheidis/lac"></a> der sinnlichen
Welt. So hat das Leben trotz der Spaltung Einen Hauptstandort und
bleibt auf Ein Hauptziel gerichtet, hier wie da ist alles Gute
geistiger Art, hier wie da liegt alles am Erringen echten Seins.
Sicherlich ist hier nicht alles ausgeglichen, innerhalb der
Gesamtbewegung verbleiben verschiedene Strömungen. Aber vielleicht
verschuldet nicht Plato allein solchen Widerspruch, vielleicht
enthält überhaupt das menschliche Leben und Wesen Antriebe
entgegengesetzter Art. Ist eine Selbständigkeit und
Ursprünglichkeit des Lebens erreichbar ohne eine Erhebung über die
Erfahrung, ist aber das dabei Gewonnene auszubauen ohne eine
Rückkehr zu ihr? Wie dem sein mag, am tiefsten eingegriffen haben
nicht die Denker, welche rasch eine Einheit erstrebten und sich in
ihr gegen alle Verwicklung verschanzten, sondern die, welche die
Gegensätze sich voll ausleben und einander hart widerstreiten
ließen; denn das ergab einen inneren Forttrieb des Lebens, der mehr
und mehr aus ihm machte. So aber ist es bei Plato geschehen.</p>
<p class="center">*</p>
<p class="center"><b class="calibre7">e. Das Gesamtbild des Menschenlebens.</b></p>
<p class="calibre2">Das Gesamtbild unseres Daseins zeigt alle Hauptrichtungen der
platonischen Arbeit. Den Gegensatz der beiden Welten teilt auch der
Mensch, indem er aus Leib und Seele besteht oder vielmehr zu
bestehen scheint. Denn in Wahrheit bildet allein die Seele unser
Selbst, dem der Körper nur äußerlich anhängt. Die Seele hat Teil an
der Welt des ewigen Seins und der reinen Schönheit, während der
Körper uns in das Reich der Sinne herabzieht und seinem Wandel
unterwirft. Die Unsterblichkeit der Seele gilt in diesen
Zusammenhängen als völlig sicher. Wo der Kern des Lebens jenseit
alles zeitlichen Werdens und aller Beziehung zur Umgebung liegt,
und die Unwandelbarkeit die Haupteigenschaft des geistigen Lebens
bildet, da muß die Seele, jede einzelne Seele, zum Grundbestande
der Wirklichkeit gehören. Sie ist nie geworden und kann nie
vergehen; die Verbindung mit dem Körper erscheint als ein bloßer
Abschnitt ihres Lebens, ja als Folge einer Schuld, eines
»intellektuellen Sündenfalls« (Rohde); von den Folgen dieser
Schuld befreie sie ernste und eifrige Lebensarbeit und führe sie,
wenn auch nach vielfacher Wanderung durch andere Körperformen,
schließlich heim zur übersinnlichen Welt.</p>
<p class="calibre2">Mit der kräftigen Durchbildung solcher Überzeugungen hat Plato
<a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page45" title="adelheidis/lac"></a> tiefen
Einfluß auf die Menschheit geübt. Einen festen Glauben an die
Unsterblichkeit führte ihm nicht schon die Umgebung zu. Denn die
alte Vorstellung von einem schattenhaften Fortleben der Seelen im
Hades – grundverschieden von einer wahren
Unsterblichkeit – beherrschte noch immer die Gemüter, selbst
Sokrates behandelte die Unsterblichkeit als ein offenes Problem.
Wohl bestanden daneben schon von alters her auch andere Strömungen,
und in kleineren, religiös und philosophisch gestimmten Kreisen
hatte ein voller Unsterblichkeitsglaube vielfach Wurzel geschlagen,
aber eine enge Verbindung dieses Glaubens mit dem Ganzen einer
Gedankenwelt hat erst Plato geschaffen.</p>
<p class="calibre2">Zugleich ist auch über die Hauptrichtung der Arbeit entschieden.
Alle Sorge geht jetzt auf den inneren Stand, auf die Befreiung und
Läuterung der Seele. Es verlangt aber die Wendung zur Wahrheit um
so mehr das Aufgebot aller Kraft, als uns zunächst die Körperwelt
mit dem Schein der Wahrheit umfängt, unsere Seele wie verschüttet
und begraben, unsere Erkenntniskraft durch die Sinnlichkeit arg
geschwächt und getrübt ist. So gilt es eine völlige Umwälzung: in
schroffem Bruch mit der ersten Lage kehre der Mensch das geistige
Auge und zugleich das ganze Wesen vom tiefen Dunkel der Nacht zum
hellen Lichte der Wahrheit. Die Bewegung des Lebens, wie alle
Bildung und Erziehung, entwickelt sich nicht aus der bloßen
Erfahrung, und der Fortgang entsteht nicht aus der Berührung von
Innerem und Äußerem, sondern die Arbeit ist ein Sichbesinnen auf
das wahre Wesen des Geistes, ein Wiederaufnehmen der echten, stets
vorhandenen, nur verdunkelten Natur. So die Lehre von der
Wiedererinnerung und den angeborenen (besser: eingeborenen)
Begriffen, angreifbar in der näheren Fassung, unangreifbar in dem
Grundgedanken, daß alles echte Leben eine Entfaltung des eigenen
Wesens bedeutet, und daß alles Äußere die geistige Betätigung nur
anregen oder fördern, nicht aber erzeugen kann. Echte Einsicht und
Tugend dem Menschen durch Sitte und Übung zuführen zu wollen, heißt
nach Plato so viel wie Blinden das Augenlicht von draußen einsetzen
zu wollen. Alles Erkennen schöpft schließlich nicht aus der
Erfahrung, sondern aus dem ewigen Wesen des Geistes. Die
»Einzeldinge sind Beispiele, welche uns an die allgemeinen Begriffe
erinnern, aber sie sind nicht das Reale, auf welches diese Begriffe
sich beziehen« (Zeller).</p>
<p class="calibre2">Mit solcher Gestaltung der Lebensaufgabe verschlingen sich
<a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page46" title="adelheidis/lac"></a> bei Plato
aufs engste Überzeugungen vom tatsächlichen Verhalten des Menschen.
Jene Wendung zum wahren Sein wird nach seiner Überzeugung von
einzelnen vollzogen, es gibt – das ist eine gemeinsame
Behauptung der griechischen Denker – echte Tugend auch
innerhalb des menschlichen Kreises. Aber jene Individuen sind
selten Ausnahmen, die Menge verharrt bei der niederen Welt und
bleibt dem Schönen fremd. Der Denker folgt hier einer um ihn
landläufigen Überzeugung, daß der Edlen wenige, der gemeinen
Naturen viele seien, aber er steigert den Gegensatz zu äußerster
Schroffheit, indem er den Philosophen ganz und gar von der übrigen
Menschheit abhebt. Philosophen sind ihm die, welche ihr ganzes
Streben und ihre ganze Liebe dem unwandelbaren Sein zuwenden, die
damit allein den rechten Maßstab der Schätzung finden, während die
übrigen Menschen am flüchtigen Werden und bloßen Scheine hangen. So
erscheint der Philosoph in den menschlichen Verhältnissen als ein
fremdes Gewächs, und wenn es gar heißt, daß die Menge mit ihrem
Hange zu sinnlichen Genüssen nach Art des Viehes lebt, der
Philosoph dagegen im Schauen ewiger Wahrheit und Schönheit ein
gottähnliches Leben führt, so droht der Zusammenhang ganz zu
zerreißen und die Menschheit in zwei getrennte Kreise
auseinanderzufallen. Und zwar für alle Zeit. Denn es fehlt hier
jeder Glaube an eine Emporhebung der Menge, wie überhaupt an einen
geistigen und sittlichen Fortschritt. Wie im All, so gilt auch bei
der Menschheit das Verhältnis von Gutem und Bösem als der
Hauptsache nach unveränderlich; das Sinnliche, die Wurzel aller
Hemmung, kann nicht vergehen, der schroffe Gegensatz von
Sinnlichkeit und Geist, von flüchtigem Werden und unwandelbarem
Sein gestattet keinen Glauben an irgendwelches Vordringen der
Vernunft. Das heißt nicht alle Bewegung und Verschiebung in unseren
Verhältnissen leugnen. Aber Plato erklärt jene im Anschluß an
ältere Denker durch die Annahme kreisläufiger Perioden, großer
Weltepochen, die wohl aus der Astronomie stammt und schließlich auf
Babylon weist. Nach Vollendung ihres Umlaufs kehren die Dinge zum
Ausgangspunkt zurück, um denselben Lauf neu zu beginnen; die
geschichtliche Bewegung wird eine endlose Aufeinanderfolge von
Kreisen ähnlichen Inhalts. Diese beharrende Ordnung im Wechsel
erscheint als ein Abbild der Ewigkeit. Solches Fehlen eines
weltgeschichtlichen Fortschritts gestattet keine Berufung von den
Mißständen der Gegenwart an eine bessere Zukunft. <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page47" title="adelheidis/lac"></a></p>
<p class="calibre2">Aber wenn der platonischen Lebensführung manche Antriebe
mangeln, die der moderne Mensch als selbstverständlich betrachtet,
so fehlen zugleich manche Zweifel und Sorgen, und für alles
Unzulängliche der Durchschnittslage bietet einen überreichen Ersatz
des Menschen geistige Natur, seine Wesensverwandtschaft mit der
Gottheit. Dem trüben Zwielicht der menschlichen Verhältnisse kann
der Tüchtige sich entwinden und sein Auge am reinen Lichte ewiger
Wahrheit weiden. Bei Einsetzung voller Kraft ist das hohe Ziel sehr
wohl erreichbar. Denn Plato kennt keine starre Kluft zwischen dem
strebenden Geist und der Wahrheit, kein Irregehen dessen, der
ernstlich sucht; wird nur das richtige Verfahren geübt, so kann der
Erfolg nicht fehlen. Wie aber ein tüchtiges und mutiges Denken die
volle Tiefe der Dinge erschließt, so beherrscht es auch alles
Handeln und erhebt das ganze Leben über die niedere Sphäre. Alles
Gelingen liegt dabei an kräftiger Tätigkeit, aber diese Tätigkeit
bleibt bei allem Ernst und Eifer frei von Aufregung und Hast, da
alles menschliche Streben aus einer Verwandtschaft unseres Wesens
mit dem ewigen Sein hervorgeht und dadurch sicher geleitet
wird.</p>
<p class="calibre2">Diese Überzeugungen seien nun in die Hauptverzweigung des Lebens
verfolgt.</p>
<p class="center">*</p>
<p class="center"><b class="calibre7">f. Die einzelnen Lebensgebiete.</b></p>
<p class="center">α. Die Religion.</p>
<p class="calibre2">Plato ist insofern eine durch und durch religiöse Natur, als das
Angewiesensein des Menschen auf das All, das seine Arbeit
beherrscht und eigentümlich gestaltet, von seiner Überzeugung
vollauf anerkannt und in persönliches Erleben verwandelt wird. Er
ist ganz davon durchdrungen, daß ein »königlicher Geist« das All
regiert. Wie sehr er sich von dem Wirken einer göttlichen Macht
umgeben weiß und fühlt, das bekundet schon seine mit Ausdrücken aus
dem Gebiet der Religion, namentlich des Kultus, durchwobene
Sprache; die Bewegungen jener Jahrhunderte zur Verinnerlichung der
Religion haben zum mindesten auf die Einkleidung seiner Lehren
stark gewirkt. Aber Platos Religion bleibt immer die Religion eines
griechischen Denkers, und zwischen dieser und der christlichen
besteht eine weite Kluft. Denn dort ist die Religion nicht eine
Rettung aus tiefer Not, nicht die Wiederherstellung der gestörten,
ja zerstörten Einheit mit dem höchsten Wesen, nicht der Trost
tieferschütterter <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page48" title="adelheidis/Konmax"></a> und schwerringender Seelen. Vielmehr dünkt
hier der ursprüngliche Zusammenhang des Menschlichen und Göttlichen
durch alle Irrung nicht so zerrissen, um sich nicht jederzeit durch
eigene Tat wieder aufnehmen zu lassen. So begleitet, ja durchdringt
die Religion alle Arbeit, sie macht dem Menschen das Leben größer
und stellt sein Wirken in unsichtbare Zusammenhänge. Das
Bewußtsein, von der Gottheit behütet und im Lebenskampf unterstützt
zu werden, erfüllt das Gemüt des Denkers mit aufrichtiger
Frömmigkeit. Aber diese Religion erzeugt nicht eine eigene Welt und
bildet daher auch nicht einen besonderen Kreis gegenüber dem
anderen Leben. Auch entsteht keine seelische Gemeinschaft zwischen
Gottheit und Mensch, nichts, was ein persönliches Verhältnis heißen
könnte, und was ein neues Leben erzeugte.</p>
<p class="calibre2">Gegenüber einer durchgängigen Erweisung des Göttlichen im All
und Menschenwesen bedarf es hier keiner besonderen geschichtlichen
Offenbarung, noch einer religiösen Lehre, einer Theologie; mit
jener Frömmigkeit ist aufs beste vereinbar ein deutliches
Bewußtsein des weiten Abstandes Gottes vom Menschen. Das
Unwandelbare und Reine werde nicht in die unlautere Sphäre des
sinnlichen Werdens herabgezogen; nur durch Zwischenstufen, durch
Dämonen, kann es zum Niederen wirken, Gott vermischt sich nicht mit
dem Menschen. So die Überzeugung des Philosophen: »Gott, den Vater
und Bildner des Alls, zu finden ist schwer und, wenn man ihn
gefunden hat, allen mitzuteilen unmöglich.«</p>
<p class="calibre2">Die nähere Ausführung dieser Religion folgt aber der zwiefachen
Richtung der platonischen Denkart. Dem Metaphysiker ist die
Forschung selbst und sie allein die wahre Religion; Gott bedeutet
das schlechthin unwandelbare und einfache Wesen, aus dem alle
Unwandelbarkeit und Einfachheit, zugleich aber alle Wahrheit
stammt, er bildet das Maß aller Dinge. Erst die Wendung vom
farbigen Abglanz zum reinen Urquell alles Lichts führt das Leben
vom Schein zur Wahrheit.</p>
<p class="calibre2">Nach der anderen Richtung ist Gott das Ideal der sittlichen
Vollkommenheit, der vollauf gerechte und gütige Geist. Gott ähnlich
werden, das heißt mit Einsicht fromm und gerecht sein; die
Frömmigkeit aber ist Gerechtigkeit gegen die Gottheit, Erfüllung
aller Schuldigkeit gegen sie. Den Kern der Überzeugung bildet hier
die Idee der sittlichen Weltordnung, der gerechten Vergeltung von
Gutem und Bösem. Indem der Philosoph diesen Grundgedanken <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page49" title="adelheidis/Konmax"></a> der
religiösen Überzeugung des Griechentums aufnimmt, erweitert und
vertieft er ihn gegen die übliche Fassung. Denn die Volksmeinung
erwartete die Vergeltung schon im Diesseits, wenn nicht am
Einzelnen, so doch an seinem Geschlecht; auch Plato läßt eine
Gerechtigkeit schon in diesem Leben walten, ihren vollen Sieg aber
erwartet er erst vom Jenseits. Er entwirft das Bild eines
Totengerichts, das unbestechlich über die Seele urteilt, wenn alle
äußere Hülle gefallen ist; er hat mit der packenden
Eindringlichkeit seiner Schilderung es der Vorstellung der
Menschheit unvergeßlich eingeprägt. Aber er richtet deshalb
keineswegs die Gedanken des Menschen vornehmlich über das Grab
hinaus. Von den Toten sollen wir denken, daß sie fortgegangen sind
nach Erfüllung ihres Geschicks; wir aber müssen für die Gegenwart
sorgen.</p>
<p class="calibre2">Die Gerechtigkeit des platonischen Glaubens wird keine Härte,
sie ist mit Milde und Gnade vereinbar. Aber die Gerechtigkeit
überwiegt hier die Liebe, und das sittliche Reich erscheint
vornehmlich als ein Welt- und Gottesstaat, nicht als ein von
väterlicher Liebe getragener und beseelter Familienkreis. Das hat
tief auf die folgenden Zeiten, namentlich auf das mittelalterliche
Christentum gewirkt.</p>
<p class="calibre2">Wie der Denker an dieser Stelle die Volksüberzeugung nicht
sowohl verläßt als vertieft, so bleibt auch darin ein Zusammenhang,
daß Plato bei aller Verfechtung einer weltbeherrschenden Einheit
keineswegs die Vielheit göttlicher Kräfte aufgibt, sondern mit
seiner inneren Belebung der Natur die mythologische Vorstellung auf
den Boden der Philosophie verpflanzt. Wo aber die Volksmeinungen
den reineren Begriffen der Philosophie widersprechen, da scheut er
nicht eine kräftige Abweisung und einen offenen Kampf. Er verwirft
alles Unlautere und Unwürdige im üblichen Bilde der Götter, er
verwirft mit noch größerem Eifer eine Religion, welche, statt sich
durch Tat und Tüchtigkeit der Gottheit zu nähern, ihre Gunst durch
äußere Werke, durch Opfer usw. erkaufen möchte und damit zu einem
Handelsgeschäfte sinkt. Nur geringe Menschen, nur Schwächlinge
werden diesen Weg betreten; in Wahrheit ist es der Tüchtige,
welcher der göttlichen Hilfe gewiß sein darf, der Gedanke an die
Gottheit, der den Bösen schreckt, wird ihn mit froher Erwartung
erfüllen. <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page50" title="adelheidis/Konmax"></a></p>
<p class="calibre2"> </p>
<p class="center">β. Der Staat.</p>
<p class="calibre2">Der weltflüchtigen Richtung Platos müßte ein ablehnendes
Verhalten zum Staat entsprechen. Wo die nächste Welt gänzlich dem
Wandel und Schein verfallen ist, und wo die durch Denkarbeit
vertiefte Persönlichkeit der gesellschaftlichen Umgebung schroff
widerspricht, da sollte der Staat den Denker nicht anziehen und zur
Mitarbeit reizen. Daß sich in Wahrheit Plato so viel mit ihm
befaßt, bezeugt, wie stark es ihn aus der reinen Gedankenwelt zum
Wirken für die Umgebung zurückzieht; es treiben aber dabei die
beiden Hauptrichtungen seiner Überzeugung nach verschiedenen
Zielen.</p>
<p class="calibre2">Die letzte Behandlung, welche die »Gesetze« bieten, darf für uns
unbeachtet bleiben, da sie bei aller Weisheit der einzelnen
Gedanken sich mehr dem Erfahrungsstande anpaßt. Dagegen müssen uns
die zwei Staatsbilder beschäftigen, welche der »Staat« überliefert.
Wohl umschließt sie beide ein einziges Werk, das gewiß die eigene
Hand des Philosophen in leidliche Einheit gefügt hat. Aber
innerlich sind beide zu verschieden, als daß ein und derselbe Guß
sie hätte bilden können. Wer so gering von menschlichen Zuständen
denkt und sich ihnen so fremd fühlt, wie der Plato der zweiten
Hälfte jenes Werkes, der kann nicht mit solchem Eifer
politisch-soziale Reformpläne schmieden und sie mit soviel Liebe
ins Einzelne ausführen, wie der Plato der ersten Hälfte. So klingen
wohl in dem Werke verschiedene Epochen der platonischen Denkarbeit
nach.</p>
<p class="calibre2">Auf der ersten Stufe finden wir Plato als einen eifrigen
Reformator des griechischen Staates in der Richtung eines
weitergebildeten Sokratismus. Der Staat wird hier – unter
ständigem Parallelismus mit der Einzelseele – eine Darstellung
des Ideals der Gerechtigkeit im großen. Zu diesem Zwecke sollen
sich alle Verhältnisse streng nach moralischer Ordnung gestalten,
vornehmlich die Erziehung; die Hauptaufgaben der Gesellschaft
sollen sich gemäß den Stufen des Seelenlebens deutlich scheiden und
in festen Ständen verkörpern; jeder soll gewissenhaft sein
besonderes Werk verrichten, alle aber sich unter der Herrschaft der
Intelligenz zu einer gemeinsamen Leistung verbinden. Um dem Dienst
dieses Ganzen nicht durch selbstische Zwecke entfremdet zu werden,
haben die Glieder der höheren Stände auf Privateigentum und Familie
<a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page51" title="adelheidis/Konmax"></a> zu
verzichten; ein wunderlicher Kommunismus aus ethischen Gründen
krönt die platonische Lehre.</p>
<p class="calibre2">So wird der Staat ein ethisches Kunstwerk, ein Reich der auf
Einsicht gegründeten Tugend. Mit der scharfen Ausprägung seiner
Züge scheint dies Bild zunächst von der Wirklichkeit völlig
abgelöst, eine genauere Betrachtung aber zeigt die Lehren des
Denkers und die griechischen Verhältnisse durch manche Fäden
verknüpft. Hier glaubt Plato noch an die Möglichkeit gründlicher
Reformen innerhalb der menschlichen und der griechischen Lage.</p>
<p class="calibre2">Der spätere Entwurf hat diese Hoffnung aufgegeben. Die Sehnsucht
nach dem Reich unwandelbarer Wesenheit hat hier den Denker dem
menschlichen Dasein völlig entfremdet. Kehrt er dahin zurück, so
geschieht es nicht zu eigener Freude, sondern der Brüder wegen, und
auch weniger in der Hoffnung auf einen Erfolg in diesem Kreise, als
um auch hier die ewigen Wahrheiten zu verkünden. Der Staat, wie er
sich hier ausnimmt, ist vornehmlich eine Anstalt zur Heranbildung
des Menschen für die höhere Welt; es gilt in geordnetem Aufstieg
die Seele nach und nach vom Sinnlichen abzulösen und dem
Unsinnlichen zuzuführen; das ganze Leben wird eine strenge
Erziehung, eine geistige Läuterung; diese Erziehung hebt den
Menschen mehr und mehr in eine Welt, der gegenüber alles politische
Leben verschwindet. Durch den Staat selbst vollzieht sich eine
Befreiung von der Sphäre, welcher der Staat angehört.</p>
<p class="calibre2">So eine Verschiedenheit, ja Unvereinbarkeit der beiden Typen.
Aber bei allem Abstand bleiben wichtige Züge gemeinsam und geben
der platonischen Politik einen einheitlichen Charakter. Hier wie da
ist der Staat im großen was der Mensch im kleinen, hat überlegene
Einsicht zu herrschen, bilden die geistigen und sittlichen Güter
den Hauptinhalt des Lebens, wird das Individuum dem Ganzen
vollständig eingefügt. Die Geringschätzung des
Durchschnittsmenschen läßt zur Fernhaltung aller Willkür feste
Gesetze verlangen, aber der Denker neigt dahin, über das Gesetz die
große Persönlichkeit zu stellen, den »mit Einsicht königlichen
Mann«.</p>
<p class="calibre2">Platos politische Theorien sind sicherlich recht angreifbar,
selbst die Gesamtrichtung enthält den Widerspruch, daß der Staat
weit über die Individuen hinausgehoben werden soll, daß diese
Hinaushebung <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page52" title="adelheidis/Konmax"></a> aber durch die Gedankenarbeit der
souveränen Persönlichkeit erfolgt, die für sich eines Staates kaum
bedarf, und die an den vorhandenen Staaten vornehmlich scharfe
Kritik übt. Aber Plato hat auch auf diesem Gebiet manche einzelne
Gedanken von bleibender Bedeutung entwickelt und eine Fülle
fruchtbarer Anregungen ausgestreut; z. B. hat er zuerst die
Bedeutung fester Berufe verfochten, eine Organisation der
Gesellschaft verlangt, den Frauen volles Recht zugesprochen, usw.
Ferner zeigt er über die besonderen Lehren hinaus in der Behandlung
politischer, gesellschaftlicher, pädagogischer Fragen eine
wunderbare Größe. Er zeigt sie in der Art, wie er in lebensvoller
Schilderung die einzelnen Staatsverfassungen auf ihren seelischen
Charakter zurückführt und dem Charakter der Gemeinschaft einen
Typus der Individuen entsprechen läßt; er zeigt sie in dem Aufweis
einer inneren Bewegung der Verfassungen, indem jede einzelne durch
ihre Steigerung über sich selbst hinaustreibt; er zeigt sie endlich
in seinen Erziehungslehren, die namentlich das feinste Verständnis
des Kindeswesens, des in ihm aufquellenden Lebens und seiner
Bedürfnisse zeigen, wie es einzigartig in der griechischen
Gedankenwelt dasteht. Auch hier erweist sich das unvergleichliche
Vermögen des Mannes, mit kühnem Aufstreben über die Welt das
liebevollste Eingehen auf die Fülle ihrer Gestalten und die
freudigste Würdigung alles Lebensvollen zu verbinden.</p>
<p class="calibre2"> </p>
<p class="center">γ. Die Kunst.</p>
<p class="calibre2">Eigentümlicherweise hat bei Plato die Kunst, welche den tiefsten
Zug seines Wesens für sich hatte, einen angemessenen
wissenschaftlichen Ausdruck nicht gefunden; ja eben der Philosoph,
der mehr als irgend ein anderer als Denker zugleich ein Künstler
war, hat die Kunst mit Anklagen überhäuft. Gegen die Kunst
verbünden sich der metaphysische und der ethische Zug seines
Wesens, während eine angemessene ästhetische Würdigung fehlt. Als
eine bloße Nachahmung der sinnlichen Welt, als ein Abbild des
Abbildes, entfernt sich die Kunst am weitesten vom wesenhaften
Sein. Anstoß erregen die bunten und wechselnden Gestalten, welche
die Kunst, namentlich das Drama, uns nachleben heißt, da uns die
eine eigene Rolle im Leben wahrlich genug zu tun gibt, Anstoß auch
der unsaubere Inhalt der vom mythologischen Vorstellungskreise
beherrschten Dichtung, Anstoß endlich die fieberhafte Aufregung der
<a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page53" title="adelheidis/lac"></a> Gefühle,
wie Plato die Kunst seiner Zeit sie mehr und mehr anstreben sah. So
erfolgt ein harter Zusammenstoß, trotz aller persönlichen Neigung
muß fallen, was das sittliche Wohl gefährdet. Ganze Kunstgattungen,
wie das Drama, werden völlig verworfen; was bleibt, hat den
Forderungen der Moral unbedingt zu gehorchen. Aber solche
moralisierende Behandlung der Kunst lasse nie vergessen, welchen
Glanz und welche Anmut Plato den Gebilden seiner Arbeit durchgängig
verliehen hat, wie er die ganze Weite der sichtbaren Welt bis in
die letzte Verzweigung hinein an sich zieht und zum Sinnbild seiner
Gedanken gestaltet, wie seine kraftvollen Bilder sich der
Menschheit unvergeßlich eingeprägt haben; denken wir nur an die
Rosse der Seele, die aufwärts oder abwärts ziehen, denken wir an
die dunkle Höhle, die uns hienieden umfängt, denken wir an die
Schilderung des Totengerichts! Auch hat das Mißtrauen gegen die
Kunst keinerlei Schwanken in der Schätzung des Schönen bewirkt.
Dieses steht bei Plato in sicherer Herrschaft über das All, von ihm
aus durchwaltet es auch das menschliche Leben und erstreckt es sein
Wirken tief auch in die Wissenschaft hinein.</p>
<p class="calibre2"> </p>
<p class="center">δ. Die Wissenschaft.</p>
<p class="calibre2">Die platonische Wissenschaft ist nämlich von der neueren
grundverschieden. Sie strebt nicht zu kleinsten Elementen, um aus
ihrer Verbindung die Wirklichkeit aufzubauen, sondern sie umfaßt
alle Mannigfaltigkeit von vornherein mit Einem Anblick; die
Erklärung geht vom Großen zum Kleinen, vom Ganzen zum Teil, von der
Synthese zur Analyse. Die Dinge »zusammenzuschauen«, die
Verwandtschaften zu erkennen, das ist für Plato das Hauptwerk des
Denkers, die schöpferische Intuition bildet die ihm eigenste
Größe.</p>
<p class="calibre2">Auch ist die platonische Wissenschaft nicht wie die neuere eine
Umsetzung der Welt in ein Reich allmählicher Entwicklung, ein
Verstehen der Wirklichkeit vom Werden her, sondern sie hebt ein
ewiges Sein aus dem Strom des Werdens heraus und legt seine Züge
für alle Zeiten fest. Die Wendung zum Wesen erfolgt dabei nicht in
langer und mühsamer Arbeit, sondern in einer durchgreifenden Tat,
unmittelbar kann geistige Kraft in das Reich der Wahrheit
versetzen. Frei von jenem nagenden Zweifel, der ihre Arbeit in der
Wurzel angreift, kann die Wissenschaft hier das Leben tragen und
mit freudiger Gewißheit erfüllen. <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page54" title="adelheidis/lac"></a></p>
<p class="calibre2">Diese Fassung des Erkennens legt allen Nachdruck auf die
Prinzipienfragen und schätzt die Einzelwissenschaften lediglich als
Wege zur Philosophie. Nur die Mathematik als die Wissenschaft,
welche im Sinnlichen selbst ein Übersinnliches ergreift, findet
mehr Anerkennung, alle Beschäftigung dagegen mit der bunten Fülle
der sinnlichen Welt dünkt von geringem Wert und alle Behauptung
darüber bloß eine mehr oder minder glaubhafte Ansicht. Dabei wird
die Natur von der Seele her gedeutet, die hier als der Quell aller
Bewegung und Ordnung im Weltall gilt; einem starken
Anthropomorphismus der Erklärung ist damit Tür und Tor geöffnet. So
hat Plato die naturwissenschaftliche Forschung aufs schwerste
geschädigt; indem ein Netz von menschlichen Begriffen die
Wirklichkeit umschlingt und eine Würdigung der Dinge in ihren
eigenen Zusammenhängen hemmt, gehen die wertvollen Ansätze zu einer
exakten Naturbegreifung, welche die vorsokratische Philosophie
enthielt, für Jahrtausende verloren. Die Stärke der platonischen
Leistung liegt in der reinen Begriffsphilosophie, der Dialektik,
die nichts von draußen her annimmt und auch über die eigenen
Grundlagen deutliche Rechenschaft gibt; Plato nennt sie eine »Gabe
der Götter und das wahre Feuer des Prometheus«. Seinen Höhepunkt
erreicht dies Verfahren in der Behandlung der allgemeinsten
Gegensätze: Ruhe und Bewegung, Einheit und Vielheit. Die Art, wie
die einzelnen Glieder sowohl für sich entwickelt als aufeinander
angewiesen werden, bildet mit dem Überschauen weitester Reihen und
dem Zusammenhalten gegenläufiger Bewegungen, mit dem sicheren
Wandeln im Reich unsinnlicher Größen, dem Aufheben aller Schwere
durch das frohe Spiel der Gedanken die vollendetste Leistung
griechischer Beweglichkeit des Geistes. Überhaupt aber ist Plato
stark darin, Gegensätze miteinander zu verschlingen und auseinander
hervorgehen zu lassen. So zeigte er es bei der Liebe, die Besitz
und Entbehren miteinander verbindet, so läßt er Schmerz aus Lust
und Lust aus Schmerz entspringen, so zerstören ihm die
Staatsverfassungen sich durch ihre eigne Steigerung, so sind es die
Widersprüche der sinnlichen Wahrnehmung, welche über sie
hinauszugehen zwingen. Durchgängig eine unerschöpfliche Fülle und
jugendliche Frische des Lebens. <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page55" title="adelheidis/lac"></a></p>
<p class="center">*</p>
<p class="center"><b class="calibre7">g. Rückblick.</b></p>
<p class="calibre2">So deutlich die einzelnen Züge der platonischen Lebensarbeit
sind, das Ganze seiner Art zu würdigen ist überaus schwer; fast
unvermeidlich fließt dabei die Individualität des Betrachters ein
und läßt einen jeden sich seinen eigenen Plato bilden. Jedenfalls
darf Plato nicht als eine überwiegend kontemplative, friedfertige,
von seliger Ruhe erfüllte Natur verstanden werden, wie es auch
Goethe in der bekannten Schilderung der Farbenlehre
tut (»Platon verhält sich zu der Welt wie ein seliger Geist,
dem es beliebt, einige Zeit auf ihr zu herbergen«). Das ist ein
begreiflicher Irrtum, aber es ist ein Irrtum. Es war wohl die
wunderbare Vollendung der Form, die Abgeklärtheit der Gestaltung,
welche den leidenschaftlichen Affekt verkennen ließ, der dieses
Lebenswerk durchlodert; indem der Zauber der Schönheit auch die
härtesten Kämpfe verklärte, verbarg er zugleich ihre Schwere. Wir
dürfen nicht übersehen, daß Plato die Umwälzung des Weltbildes und
die Umwertung der Lebensgüter keineswegs fertig vorfand, sondern
sie selbst zu vollziehen hatte; das konnte nicht in Fluß kommen
ohne starke Erregungen und Bewegungen, es konnte nicht gelingen
ohne ein Überwinden härtester Widerstände und eine Erweisung
gigantischer Kraft. Wie mußte es in der Seele des Denkers wogen,
wenn er Bewegungen hervorrief, die ganze Jahrtausende durchzittern
und immer von neuem Liebe und Haß entzünden! Aber verschiedenes
wirkt bei Plato zusammen, diesen Affekt vor aller wilden
Leidenschaft zu behüten, wie sie z. B. oft Augustins großes
Schaffen entstellt. Das ist die Vornehmheit der Gesinnung, die über
die individuelle Stimmung hinaus an dem Ewigkeitsgehalt der alles
menschliche Leben und Streben beherrschenden Ideenwelt einen
sicheren Halt und eine feste Grundlage hat. Das ist ferner die
starke Sehnsucht, die alle Arbeit Platos durchdringt und die
Schroffheit der Gedanken mildert. Das ist endlich und vor allem das
ursprüngliche, immer frisch aufquellende Leben, das überallhin
Leben trägt, überall Leben aufdeckt, das, was es ergreift, seiner
tiefsten Seele zuführt und so die ganze Weite in wunderbarer Weise
verklärt. Alle echte Philosophie ist ein Ringen des Ganzen einer
Persönlichkeit mit dem Ganzen des Alls, aber nirgends ist dies
Ringen gewaltiger und fruchtbarer als bei Plato, den es einmal von
innen heraus so weit über den ersten Befund der Dinge hinaustreibt,
und der auf der <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page56" title="adelheidis/lac"></a> erreichten Höhe doch jenen mit all seiner
Fülle fest im Auge behält und ihn in den Dienst der letzten Ziele
stellt. So bleibt Plato bei diesem Hauptpunkte unerreicht, ein
Vorbild aller kommenden Zeiten.</p>
<p class="calibre2">Plato bildet die Höhe der gesamten griechischen Geistesarbeit,
indem in ihm ihre beiden Hauptrichtungen, das Erkenntnisverlangen
und der Gestaltungstrieb, das wissenschaftliche und das
künstlerische Streben, sich miteinander verbinden und durcheinander
steigern. Seine Lebensanschauung hat den eigentümlichen Typus des
griechischen Idealismus zu klarer Ausprägung gebracht. Dieser Typus
verflicht untrennbar die Überzeugungen, daß der mutigen Arbeit des
Denkens sich eine neue Welt wahrhaftigen Wesens und echten Glückes
eröffnet, daß diese Welt mit der nächsten Wirklichkeit unablässig
zusammenstößt und ihren Widerstand nie völlig brechen kann, daß sie
aber mit ihrem in sich selbst gegründeten Leben allen Angriffen
weit überlegen bleibt und durch ihre lautere Wahrheit und Schönheit
den Menschen über das Gebiet des Kampfes und Leides sicher
hinaushebt. – Eine Verwandtschaft dieser Denkart mit der
später vom Christentum aufgebrachten ist ebenso unverkennbar, wie
ein weites Auseinandergehen innerhalb des gemeinsamen Rahmens. Hier
wie da gilt es eine neue Welt zu erringen, und es verbleiben zwei
Welten nebeneinander, aber bei Plato führt zu jener das Denken, im
Christentum die Wandlung der Gesinnung; hier wie da wirkt
Göttliches in unserm Dasein, aber bei Plato wirkt es gleichmäßig
durch alle Orte und Zeiten, in der Natur sowohl als im Menschen,
das Christentum läßt seine Mitteilung an Einem Punkt des
menschlichen und geschichtlichen Lebens gipfeln und eröffnet
zugleich eine Geschichte großen Stiles, die Plato nicht kennt und
die er ablehnen müßte.</p>
<p class="calibre2">Die unerschöpfliche Wirkung Platos erklärt sich ebenso aus dem
ursprünglichen Leben, das alle seine Arbeit beseelt, wie aus dem
Reichtum der Bewegungen, die bei ihm sich frei entfalten und alles
Ergriffene ins Große gestalten. So hat die platonische Philosophie
durch den ganzen Lauf der Geschichte aufs förderlichste zur Weckung
der Geister gewirkt, sie hat allem Sinken ins Schulmäßige und
Pedantische widerstanden, sie hat den Sinn vom Kleinen ins Große,
vom Engen ins Weite und Freie gewandt, sie hat das Leben in
frischem Fluß gehalten. Zugleich hat ihr Reichtum verschiedenen
Zeiten verschiedenes geboten. Im späteren Altertum <a class="pcalibre1 pcalibre2 pcalibre pcalibre3" id="page57" title="adelheidis/lac"></a> wurde Plato der Führer
derer, die das wachsende religiöse Verlangen auf dem Wege der
Philosophie zu befriedigen suchten; hier galt er als priesterlicher
Verkündiger der Weisheit, die den Menschen vom berückenden
Sinnenschein ablöst und seine Gedanken zur ewigen Heimat
zurücklenkt. Derselbe Philosoph aber ward mit seiner Lebensfülle,
seiner künstlerischen Anmut, seiner jugendlichen Freude am
Gestalten der Lieblingsdenker der Renaissance, gern gaben ihre
Meister sich als treue Schüler von ihm. Und zeigen nicht Männer wie
Winckelmann, Schleiermacher, Boeckh, daß Plato bis in unsere
Jahrhunderte fortwirkt? So wob sein Lebenswerk ein Band zwischen
allen Zeiten, und das Wort eines spätgriechischen Denkers: »Die
platonische Anmut bleibt ewig jung« hat auch jetzt noch ein gutes
Recht.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0005.vue?41f5b716